// Utility functions
const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => document.querySelectorAll(selector);

// Common selectors
const SELECTORS = {
  wrapper: ".custom-select-wrapper",
  addToCartForm: 'form[action="/cart/add"]',
  addToCartButton: "#add-to-cart-button",
  productSwatches: ".swatches .product-switch",
  progressBar: ".progress-bar-value",
  embroiderySummary: "[data-embroidery-summary]",
  stampedWidget: "#stamped-main-widget",
  gallery: ".product-gallery",
  scrollIndicator: ".scroll-indicator-bar",
};

// Gallery Scroll Indicator
class GalleryScrollIndicator {
  constructor() {
    this.gallery = $(SELECTORS.gallery);
    this.indicator = $(SELECTORS.scrollIndicator);
    this.boundUpdateIndicator = this.updateScrollIndicator.bind(this);
    this.init();
  }

  init() {
    if (!this.gallery || !this.indicator) return;

    this.imageCount = this.gallery.children.length;
    this.bindEvents();
    this.updateScrollIndicator();
  }

  bindEvents() {
    this.gallery.addEventListener("scroll", this.boundUpdateIndicator);
    window.addEventListener("resize", this.boundUpdateIndicator);
  }

  updateScrollIndicator() {
    const scrollPercentage =
      (this.gallery.scrollLeft /
        (this.gallery.scrollWidth - this.gallery.clientWidth)) *
      100;
    const translateX = scrollPercentage * (this.imageCount - 1);
    this.indicator.style.transform = `translateX(${translateX}%)`;
  }

  cleanup() {
    if (!this.gallery) return;
    this.gallery.removeEventListener("scroll", this.boundUpdateIndicator);
    window.removeEventListener("resize", this.boundUpdateIndicator);
  }
}

// Product Swatch Preloader
class ProductPreloader {
  constructor() {
    this.cache = new Map();
    this.init();
  }

  init() {
    // Start preloading product swatch links
    this.preloadProductSwitches();
    // Set up intersection observer for lazy loading
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      rootMargin: "50px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const link = entry.target;
          this.preloadProduct(link.href);
          observer.unobserve(link);
        }
      });
    }, options);

    $$(SELECTORS.productSwatches).forEach((link) => {
      observer.observe(link);
    });
  }

  async preloadProductSwitches() {
    const links = $$(SELECTORS.productSwatches);
    // Prioritize adjacent swatches first
    const currentSwatch = $(".swatch__item--current");
    if (currentSwatch) {
      const adjacentSwatches = [
        currentSwatch.previousElementSibling,
        currentSwatch.nextElementSibling,
      ].filter(Boolean);

      for (const swatch of adjacentSwatches) {
        const link = swatch.querySelector("a");
        if (link) await this.preloadProduct(link.href);
      }
    }
  }

  async preloadProduct(url) {
    if (this.cache.has(url)) return this.cache.get(url);

    try {
      const response = await fetch(url);
      const html = await response.text();
      this.cache.set(url, html);
      return html;
    } catch (error) {
      console.error("Error preloading product:", error);
      return null;
    }
  }

  getFromCache(url) {
    return this.cache.get(url);
  }
}

// Loading State Manager
class LoadingStateManager {
  constructor() {
    this.progressBar = $(SELECTORS.progressBar);
    this.container = this.progressBar?.closest(".progress-bar-container");
  }

  show() {
    if (!this.container) return;
    this.container.classList.remove("hidden");
    this.progressBar.style.width = "0%";
    // Trigger reflow
    this.progressBar.offsetHeight;
    this.progressBar.style.width = "90%";
  }

  complete() {
    if (!this.progressBar) return;
    this.progressBar.style.width = "100%";
    setTimeout(() => {
      if (this.container) this.container.classList.add("hidden");
      this.progressBar.style.width = "0%";
    }, 300);
  }
}

// Product Switcher
class ProductSwitcher {
  constructor() {
    this.preloader = new ProductPreloader();
    this.loadingState = new LoadingStateManager();
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener("click", async (e) => {
      const productSwitch = e.target.closest(SELECTORS.productSwatches);
      if (productSwitch) {
        e.preventDefault();
        await this.switchProduct(productSwitch);
      }
    });
  }

  async switchProduct(link) {
    this.loadingState.show();

    try {
      const url = link.href;
      let html =
        this.preloader.getFromCache(url) ||
        (await this.preloader.preloadProduct(url));

      if (!html) return;

      // Parse the new HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Update necessary elements
      this.updateMainContent(doc);
      await this.updateRelatedProductsSection(url, doc);
      await this.updateShopTheLookSection(url, doc);
      await this.updateProductHighlightsSection(url, doc);
      this.updateURL(url);

      // Reinitialize components
      window.dispatchEvent(new CustomEvent("productSwitched"));
      window.refreshLazyImages();

      // Reinitialize third-party scripts
      this.reinitializeThirdPartyScripts();
    } catch (error) {
      console.error("Error switching product:", error);
    } finally {
      this.loadingState.complete();
    }
  }

  reinitializeThirdPartyScripts() {
    // Update Stamped widget product ID before reinitializing
    const stampedWidget = $(SELECTORS.stampedWidget);
    const swatchItem = $(".swatch__item--current");
    if (stampedWidget && swatchItem) {
      const newProductId = swatchItem.getAttribute("swatch-product-id");
      if (newProductId) {
        stampedWidget.setAttribute("data-product-id", newProductId);
      }
    }

    // Check for Stamped and reinitialize
    if (window.StampedFn) {
      if (window.StampedFn.reloadUGC) {
        window.StampedFn.reloadUGC();
      }
    }

    // Check for KiwiSizing
    if (window.ks && window.ks.loadSizing) {
      window._ks_sizingLoaded = false; // Reset the initialization flag
      const kiwiData = window.KiwiSizing?.data || {};
      window.ks.loadSizing({
        productData: kiwiData,
        options: {},
      });
    }

    // Check for Swym
    // if (window.SwymCallbacks && window.SwymCallbacks.onProductPageLoaded) {
    //     window.SwymCallbacks.onProductPageLoaded();
    // }
    // if (window._swat) {
    //     let swat = window._swat;
    //     swat.productEts.makeCacheStale();
    //     let pageData = swat.getCurrentPageData();
    //     if (pageData) {
    //         swat.api.trackPageview(pageData);
    //     }
    //     if (window.Shopify) {
    //         window.initSwymShopify(true);
    //     }
    //     if (swat.ui && swat.ui.loadSecondaryStyles) {
    //         swat.ui.loadSecondaryStyles();
    //     }
    //     if (swat.initCustomLaunchPoints) {
    //         swat.initCustomLaunchPoints();
    //     }
    // }

    // If no direct reinitialization methods available, trigger a custom event
    window.dispatchEvent(new CustomEvent("thirdPartyScriptsReinit"));
  }

  updateMainContent(newDoc) {
    // Update main product section - adjust selectors as needed
    const replaced = [];
    [
      '[data-section="main-product"]',
      ".product-gallery",
      ".product-media-thumbnails",
      ".product-gallery-dots",
      ".product-info",
      ".product__mobile-header",
    ].forEach((selector) => {
      const oldContent = $(selector);
      const newContent = newDoc.querySelector(selector);
      if (oldContent && newContent) {
        oldContent.replaceWith(newContent);
        replaced.push(newContent);
      }
    });

    // Re-init carousels and nudge layout in newly replaced content
    if (replaced.length) {
      replaced.forEach((root) => {
        try {
          if (typeof window.initCarousel === 'function') {
            window.initCarousel(root);
          } else if (typeof Carousel !== 'undefined') {
            try { new Carousel(root); } catch (e) {}
          }
        } catch (e) {}

        const scrollers = root.querySelectorAll?.('.js-carousel, .product-gallery');
        scrollers?.forEach((scroller) => {
          try {
            scroller.dispatchEvent(new Event('scroll'));
            requestAnimationFrame(() => scroller.dispatchEvent(new Event('scroll')));
          } catch (e) {}
        });
      });

      // Trigger resize after frame to recompute widths on mobile
      requestAnimationFrame(() => {
        try { window.dispatchEvent(new Event('resize')); } catch (e) {}
      });
    }
  }

  async updateRelatedProductsSection(url, newDoc) {
    const container = document.getElementById('shopify-section-related-products');
    if (!container) return;

    try {
      const sectionsUrl = `${url}${url.includes('?') ? '&' : '?'}sections=related-products`;
      const response = await fetch(sectionsUrl, { credentials: 'same-origin' });

      let newSectionEl = null;

      if (response.ok) {
        const json = await response.json();
        const sectionHTML = json['related-products'];
        if (sectionHTML) {
          const tmp = document.createElement('div');
          tmp.innerHTML = sectionHTML;
          newSectionEl = tmp.firstElementChild || tmp;
          container.replaceWith(newSectionEl);
        }
      }

      // Fallback to DOM replacement
      if (!newSectionEl) {
        const parsed = newDoc.getElementById('shopify-section-related-products');
        if (!parsed) return;
        newSectionEl = parsed.cloneNode(true);
        container.replaceWith(newSectionEl);
      }

      // Initialize the Carousel specifically for this new wrapper
      if (typeof Carousel !== 'undefined') {
        try { new Carousel(newSectionEl); } catch (e) { console.warn('Carousel init error', e); }
      }

      // Nudge scroller-dependent logic
      const scroller = newSectionEl.querySelector('.js-carousel');
      if (scroller) {
        scroller.dispatchEvent(new Event('scroll'));
        requestAnimationFrame(() => scroller.dispatchEvent(new Event('scroll')));
      }
      // Also call global initializer if present
      if (typeof window.initCarousel === 'function') {
        try { window.initCarousel(newSectionEl); } catch (e) {}
      }
      requestAnimationFrame(() => { try { window.dispatchEvent(new Event('resize')); } catch (e) {} });

      // Broadcast lifecycle for any listeners
      window.dispatchEvent(new CustomEvent('section:reloaded', {
        detail: { section: 'related-products', element: newSectionEl }
      }));
    } catch (err) {
      console.error('Failed to update related products:', err);
    }
  }

  updateURL(url) {
    window.history.pushState({ path: url }, "", url);
  }

  async updateShopTheLookSection(url, newDoc) {
    try {
      const container = document.getElementById('shopify-section-shop-the-look') || 
                      document.querySelector('.shop-the-look-container') || 
                      document.getElementById('shop-the-look') ||
                      document.querySelector('[data-section-type="shop-the-look"]');
      
      if (!container) return;

      const sectionsUrl = `${url}${url.includes('?') ? '&' : '?'}sections=shop-the-look`;
      let newElement;

      try {
        const res = await fetch(sectionsUrl, { 
          credentials: 'same-origin',
          headers: {'X-Requested-With': 'XMLHttpRequest'}
        });
        
        if (res.ok) {
          const json = await res.json();
          const temp = document.createElement('div');
          temp.innerHTML = json['shop-the-look'];
          newElement = temp.firstElementChild;
        }
      } catch {
        // Fallback silently
      }

      if (!newElement) {
        newElement = newDoc.getElementById('shopify-section-shop-the-look') || 
                    newDoc.querySelector('.shop-the-look-container') || 
                    newDoc.getElementById('shop-the-look');
        if (newElement) newElement = newElement.cloneNode(true);
      }

      if (!newElement) return;

      container.replaceWith(newElement);

      // Batch initialization
      requestAnimationFrame(() => {
        try {
          if (typeof Modal !== 'undefined') new Modal();
          if (typeof window.initCarousel === 'function') window.initCarousel(newElement);
          // Nudge any scrollers inside
          newElement.querySelectorAll?.('.js-carousel')?.forEach((scroller) => {
            try {
              scroller.dispatchEvent(new Event('scroll'));
              requestAnimationFrame(() => scroller.dispatchEvent(new Event('scroll')));
            } catch (e) {}
          });
          requestAnimationFrame(() => { try { window.dispatchEvent(new Event('resize')); } catch (e) {} });

          window.dispatchEvent(new CustomEvent('section:reloaded', {
            detail: { section: 'shop-the-look', element: newElement }
          }));
        } catch (err) {}
      });

    } catch (err) {}
  }

  async updateProductHighlightsSection(url, newDoc) {
    try {
      const container = document.getElementById('shopify-section-product-highlights') ||
                      document.querySelector('[data-section-type="product-highlights"]') ||
                      document.querySelector('.product-highlights, .product__highlights');
      if (!container) return;

      let newElement;

      // Try to fetch updated section via AJAX
      const sectionsUrl = `${url}${url.includes('?') ? '&' : '?'}sections=product-highlights`;
      const res = await fetch(sectionsUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (res.ok) {
        const json = await res.json();
        const temp = document.createElement('div');
        temp.innerHTML = json['product-highlights'];
        newElement = temp.firstElementChild || temp;
      } else {
        // Fallback to parsing from fetched document
        newElement = newDoc.getElementById('shopify-section-product-highlights') ||
                    newDoc.querySelector('[data-section-type="product-highlights"]') ||
                    newDoc.querySelector('.product-highlights, .product__highlights');
        if (newElement) newElement = newElement.cloneNode(true);
      }

      if (!newElement) return;

      container.replaceWith(newElement);

      // Initialize any carousels within highlights
      try {
        if (typeof window.initCarousel === 'function') window.initCarousel(newElement);
        else if (typeof Carousel !== 'undefined') { try { new Carousel(newElement); } catch (e) {} }
      } catch (e) {}
      const scrollers = newElement.querySelectorAll?.('.js-carousel');
      scrollers?.forEach((scroller) => {
        try {
          scroller.dispatchEvent(new Event('scroll'));
          requestAnimationFrame(() => scroller.dispatchEvent(new Event('scroll')));
        } catch (e) {}
      });
      requestAnimationFrame(() => { try { window.dispatchEvent(new Event('resize')); } catch (e) {} });
      
      window.dispatchEvent(new CustomEvent('section:reloaded', {
        detail: { section: 'product-highlights', element: newElement }
      }));
    } catch (err) {
      console.error('Failed to update product highlights:', err);
    }
  }
}

// Custom Select Component
class CustomSelect {
  constructor() {
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.init();
  }

  cleanup() {
    document.removeEventListener("click", this.boundOutsideClick);
  }

  init() {
    const wrapper = $(SELECTORS.wrapper);
    // If no wrapper exists, this is likely a single variant product
    if (!wrapper) {
      // Enable add to cart button for single variant products
      const addToCartButton = $(SELECTORS.addToCartButton);
      if (addToCartButton && addToCartButton.disabled) {
        addToCartButton.disabled = false;
      }
      return;
    }

    this.cleanup();

    const newWrapper = wrapper.cloneNode(true);
    wrapper.parentNode.replaceChild(newWrapper, wrapper);

    this.elements = {
      wrapper: newWrapper,
      select: newWrapper.querySelector(".custom-select"),
      nativeSelect: newWrapper.querySelector(".native-select"),
      label: newWrapper.querySelector(".custom-select__label"),
      addToCartButton: $(SELECTORS.addToCartButton),
    };

    this.setupInitialState();
    this.bindEvents();
  }

  setupInitialState() {
    if (this.elements.addToCartButton) {
      // Do not disable CTA by default when size buttons exist; allow click to show guidance
      if (this.elements.nativeSelect) {
        const hasSizeButtons = document.querySelector('.size-button');
        if (!hasSizeButtons) {
          this.elements.addToCartButton.disabled = !this.elements.nativeSelect.value;
        } else {
          this.elements.addToCartButton.disabled = false;
        }
      }
    }
  }

  bindEvents() {
    if (!this.elements?.wrapper) return;

    this.elements.wrapper.addEventListener(
      "click",
      this.handleClick.bind(this)
    );
    document.addEventListener("click", this.boundOutsideClick);
    this.elements.select.addEventListener(
      "keydown",
      this.handleKeydown.bind(this)
    );
  }

  handleClick(e) {
    const option = e.target.closest(".custom-select__option");
    const select = e.target.closest(".custom-select");

    if (select) {
      this.toggleDropdown();
    }

    if (option && !option.classList.contains("is-disabled")) {
      this.selectOption(option);
    }
  }

  handleOutsideClick(e) {
    if (!e.target.closest(SELECTORS.wrapper)) {
      this.closeDropdown();
    }
  }

  handleKeydown(e) {
    if (!this.elements?.select) return;

    const options = Array.from(
      this.elements.select.querySelectorAll(
        ".custom-select__option:not(.is-disabled)"
      )
    );
    const currentIndex = options.findIndex(
      (option) => option === document.activeElement
    );

    const keyActions = {
      Enter: () => this.handleEnterKey(options),
      " ": () => this.handleEnterKey(options),
      Escape: () => this.closeDropdown(),
      ArrowDown: () => this.handleArrowKey(currentIndex, 1, options),
      ArrowUp: () => this.handleArrowKey(currentIndex, -1, options),
    };

    if (keyActions[e.key]) {
      e.preventDefault();
      keyActions[e.key]();
    }
  }

  handleEnterKey(options) {
    if (document.activeElement === this.elements.select) {
      this.toggleDropdown();
      if (this.isOpen()) {
        options[0]?.focus();
      }
    } else if (
      document.activeElement.classList.contains("custom-select__option")
    ) {
      document.activeElement.click();
    }
  }

  handleArrowKey(currentIndex, direction, options) {
    if (!this.isOpen()) {
      this.openDropdown();
      options[0]?.focus();
    } else {
      const newIndex = currentIndex + direction;
      if (newIndex >= 0 && newIndex < options.length) {
        options[newIndex].focus();
      }
    }
  }

  toggleDropdown() {
    if (!this.elements?.select) return;
    this.elements.select.classList.toggle("is-open");
    this.updateAriaExpanded();
  }

  openDropdown() {
    if (!this.elements?.select) return;
    this.elements.select.classList.add("is-open");
    this.updateAriaExpanded();
  }

  closeDropdown() {
    if (!this.elements?.select) return;
    this.elements.select.classList.remove("is-open");
    this.updateAriaExpanded();
  }

  isOpen() {
    return this.elements?.select?.classList.contains("is-open");
  }

  updateAriaExpanded() {
    if (!this.elements?.select) return;
    this.elements.select.setAttribute("aria-expanded", this.isOpen());
  }

  selectOption(option) {
    if (!option || !this.elements?.nativeSelect) return;

    const value = option.dataset.value;
    const text = option.textContent.trim();

    this.elements.nativeSelect.value = value;
    this.elements.nativeSelect.dispatchEvent(new Event("change"));
    this.elements.label.textContent = text;
    this.closeDropdown();

    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.disabled = !value;
    }

    this.updateAriaSelected(option);
  }

  updateAriaSelected(selectedOption) {
    if (!this.elements?.wrapper) return;
    this.elements.wrapper
      .querySelectorAll(".custom-select__option")
      .forEach((opt) => {
        opt.setAttribute("aria-selected", opt === selectedOption);
      });
  }
}

// Cart Management
class CartManager {
  constructor() {
    console.log("CartManager initialized");
    this.bindEvents();
  }

  bindEvents() {
    console.log("Binding cart events");
    const form = $(SELECTORS.addToCartForm);
    if (form) {
      const addToCartButton = form.querySelector('button[type="submit"]');
      if (addToCartButton) {
        console.log("Add to cart button found, binding click event");
        addToCartButton.addEventListener(
          "click",
          this.handleAddToCart.bind(this)
        );
      } else {
        console.warn("Add to cart button not found in form");
      }
    } else {
      console.warn("Add to cart form not found");
    }
  }

  async handleAddToCart(e) {
    console.log("Add to cart clicked");
    e.preventDefault();
    const form = e.target.closest("form");
    if (!form) {
      console.warn("Form not found in click handler");
      return;
    }

    // Require a size selection if size options exist
    const sizeButtons = document.querySelectorAll('.size-button');
    const hasSizeOptions = !!sizeButtons && sizeButtons.length > 0;
    const selectedSize = document.querySelector('.size-button.is-selected');
    if (hasSizeOptions && !selectedSize) {
      const stockStatus = document.querySelector('.selected-variant-stock-status');
      if (stockStatus) {
        stockStatus.textContent = 'Please select a size';
        stockStatus.style.display = 'block';
        stockStatus.classList.remove('hover-status');
        stockStatus.style.color = '#b00';
      }
      if (window.innerWidth <= 640) {
        const firstSizeButton = document.querySelector('.size-button');
        if (firstSizeButton) {
          const scrollTarget = firstSizeButton.closest('.size-buttons, .product-sizes, .sizes, .product-options, .product-info') || firstSizeButton.parentElement || firstSizeButton;
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            try { firstSizeButton.focus({ preventScroll: true }); } catch (e) { firstSizeButton.focus(); }
          }, 300);
        }
      }
      return; // Block add-to-cart until size selected
    }

    await this.processAddToCart(e.target, form);
  }

  async processAddToCart(button, form) {
    console.log("Processing add to cart");
    const originalButtonText = button.innerHTML;
    this.updateButtonState(button, true, "<span>Adding...</span>");

    try {
      const items = await this.buildCartItems(form);
      console.log("Items to be added to cart:", items);
      const result = await this.addItemsToCart(items);

      if (result) {
        console.log("Items added successfully, dispatching events");
        console.log("MiniCart instance:", window.miniCart);

        // window.miniCart?.reinitialize();
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("cart:update", {
              detail: { items: result },
            })
          );
          console.log("cart:update event dispatched");

          window.dispatchEvent(new CustomEvent("cart:open"));
          console.log("cart:open event dispatched");
        }, 100);
      } else {
        console.warn("Add to cart returned no result");
      }
    } catch (error) {
      console.error("Error in processAddToCart:", error);
    } finally {
      this.updateButtonState(button, false, originalButtonText);
    }
  }

  updateButtonState(button, disabled, html) {
    if (!button) {
      console.warn("Button not found for state update");
      return;
    }
    button.disabled = disabled;
    button.innerHTML = html;
    console.log(`Button state updated - disabled: ${disabled}`);
  }

  async buildCartItems(form) {
    console.log("Building cart items from form");
    const formData = new FormData(form);
    const items = [];

    // Build main product item
    const mainItem = {
      id: formData.get("id") || formData.get("id[]"),
      quantity: parseInt(formData.get("quantity")) || 1,
      properties: {},
    };

    console.log("Main item initial build:", mainItem);

    // Handle subscription
    const purchaseType = formData.get("purchase-type");
    if (purchaseType === "subscribe") {
      const sellingPlan = formData.get("selling_plan");
      if (sellingPlan) {
        mainItem.selling_plan = sellingPlan;
        console.log("Added selling plan:", sellingPlan);
      }
    }

    // Get embroidery data if it exists
    const embroideryData = this.getEmbroideryData();
    console.log("Retrieved embroidery data:", embroideryData);

    if (embroideryData) {
      // Add embroidery properties to main item
      mainItem.properties = {
        ...mainItem.properties,
        ...embroideryData.properties,
        _embroidery_id: embroideryData.embroideryId,
      };

      console.log("Updated main item with embroidery properties:", mainItem);

      // Add embroidery variant first if it exists
      if (embroideryData.variantId) {
        const embroideryItem = {
          id: embroideryData.variantId,
          quantity: 1,
          properties: {
            _embroidery_id: embroideryData.embroideryId,
          },
        };
        items.push(embroideryItem);
        console.log("Added embroidery item:", embroideryItem);
      }
    }

    // Add main item after embroidery item
    items.push(mainItem);
    console.log("Final items array:", items);

    return items;
  }

  getEmbroideryData() {
    console.log("Getting embroidery data");
    const summary = $(SELECTORS.embroiderySummary);
    if (!summary) {
      console.log("No embroidery summary found");
      return null;
    }

    const variantId = summary.querySelector(
      'input[name="embroidery_variant_id"]'
    )?.value;
    if (!variantId) {
      console.log("No embroidery variant ID found");
      return null;
    }

    const properties = {};
    const embroideryId = summary.querySelector(
      '[data-property="_embroidery_id"]'
    )?.dataset.propertyValue;

    // Get all property values except _embroidery_id
    summary
      .querySelectorAll('[data-property]:not([data-property="_embroidery_id"])')
      .forEach((item) => {
        const key = item.dataset.property;
        const value = item.dataset.propertyValue;
        if (key && value) {
          properties[key] = value;
          console.log(`Found embroidery property: ${key} = ${value}`);
        }
      });

    const data = {
      properties,
      variantId,
      embroideryId,
    };

    console.log("Collected embroidery data:", data);
    return data;
  }

  async addItemsToCart(items) {
    if (!items.length) {
      console.warn("No items to add to cart");
      return null;
    }

    console.log("Sending add to cart request:", items);
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed with status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Cart add response:", result);
      return result;
    } catch (error) {
      console.error("Error in addItemsToCart:", error);
      throw error;
    }
  }
}

// Product Page Controller
const ProductPageController = (() => {
  let instance;
  let galleryIndicator;

  return {
    init() {
      console.log("Initializing ProductPageController");
      instance = new (class ProductPage {
        constructor() {
          this.customSelect = null;
          this.cartManager = null;
          this.productSwitcher = null;
          this.init();
        }

        init() {
          this.customSelect = new CustomSelect();
          this.cartManager = new CartManager();
          this.productSwitcher = new ProductSwitcher();
          if (galleryIndicator) {
            galleryIndicator.cleanup();
          }
          galleryIndicator = new GalleryScrollIndicator();
          console.log("ProductPage components initialized");
        }

        reinitialize() {
          console.log("Reinitializing ProductPage components");
          this.customSelect?.init();
          this.cartManager?.bindEvents();
          if (galleryIndicator) {
            galleryIndicator.cleanup();
          }
          galleryIndicator = new GalleryScrollIndicator();

          // Re-init UI components that lose listeners after DOM replacement
          // Ensures "Shop the Look" modal, toggles, gallery, and size buttons work after swatch changes
          try {
            new SizeButtons();
            new Accordion();
            new Modal();
            new ToggleContainer();
            new Gallery();
          } catch (e) {
            console.warn("Reinit of UI components encountered an issue:", e);
          }
        }
      })();
    },
    reinitialize() {
      console.log("Reinitialize called on ProductPageController");
      instance?.reinitialize();
    },
  };
})();

// Initialize
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    console.log("DOMContentLoaded - initializing ProductPageController");
    ProductPageController.init();
  });
} else {
  console.log("Document already loaded - initializing ProductPageController");
  ProductPageController.init();
}

// Expose reinitialize function
window.reinitializeProduct = ProductPageController.reinitialize;

// Listen for product switch events
window.addEventListener("productSwitched", () => {
  console.log("Product switched event received");
  window.reinitializeProduct();
});

// Also reinitialize when the related products section is replaced
document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        const added = Array.from(m.addedNodes || []);
        if (added.some(n => n.id === 'shopify-section-related-products')) {
          if (typeof window.initCarousel === 'function') {
            try { window.initCarousel(document.getElementById('shopify-section-related-products')); } catch (e) {}
          }
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
});

/**
 * Product Page Interactive Components
 * Author: LimeSharp
 *
 * This script powers the interactive elements of the product page:
 * - Accordion: Toggle FAQ/product info sections.
 * - Modal: Open/close "Shop the Look" or similar modals.
 * - ToggleContainer: Switch between product pairing containers (e.g., AM/PM).
 * - Gallery: Scroll product gallery to the selected thumbnail.
 *
 * Features:
 * - Lazy initialization (only runs if elements exist).
 * - Optimized for performance and readability.
 * - Modular, reusable classes.
 */

/**
 * SizeButtons Class
 */
class SizeButtons {
  constructor({
    buttonSelector = ".size-button",
    selectSelector = ".native-select",
    addToCartSelector = "#add-to-cart-button",
    stockStatusSelector = ".selected-variant-stock-status",
  } = {}) {
    this.buttons = document.querySelectorAll(buttonSelector);
    this.select = document.querySelector(selectSelector);
    this.addToCartButton = document.querySelector(addToCartSelector);
    this.stockStatus = document.querySelector(stockStatusSelector);

    if (!this.buttons.length || !this.select) return;

    this.stockMessages = {
      "last-one": "Last one in stock!",
      "last-few": "Only a few left!",
      "sold-out": "Sold out",
      "select-size": "Please select a size",
    };

    this.init();
  }

  init() {
    this.bindEvents();
    this.bindVariantUpdates();

    // Default ATC state: require a size selection first
    const selected = document.querySelector(".size-button.is-selected");
    if (!selected && this.addToCartButton) {
      this.setAtcSelectSizeStyle(true);
    }
  }

  bindEvents() {
    this.buttons.forEach((button) => {
      // Click events
      button.addEventListener("click", () => {
        if (!button.classList.contains("is-disabled")) {
          this.handleSelection(button);
        }
      });

      // Hover events
      button.addEventListener("mouseenter", () => {
        if (!button.classList.contains("is-disabled")) {
          this.updateStockStatus(button.getAttribute("data-value"), true);
        }
      });

      button.addEventListener("mouseleave", () => {
        const selected = document.querySelector(".size-button.is-selected");
        if (selected) {
          this.updateStockStatus(selected.getAttribute("data-value"), false);
        } else {
          this.hideStockStatus();
        }
      });

      // Keyboard support
      button.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!button.classList.contains("is-disabled")) {
            this.handleSelection(button);
          }
        }
      });
    });
  }

  handleSelection(button) {
    this.deselectAll();
    this.selectButton(button);

    const variantId = button.getAttribute("data-value");
    this.updateSelectValue(variantId);
    this.updateStockStatus(variantId, false);
    this.updateAddToCartButton(variantId);
    this.triggerVariantChange(variantId);
  }

  deselectAll() {
    this.buttons.forEach((btn) => {
      btn.classList.remove("is-selected");
      btn.setAttribute("aria-selected", "false");
    });
  }

  selectButton(button) {
    button.classList.add("is-selected");
    button.setAttribute("aria-selected", "true");
  }

  updateSelectValue(variantId) {
    this.select.value = variantId;
  }

  updateStockStatus(variantId, isHover = false) {
    if (!this.stockStatus) return;

    const button = document.querySelector(
      `.size-button[data-value="${variantId}"]`
    );
    if (!button) {
      this.hideStockStatus();
      return;
    }

    const stockStatus = button.getAttribute("data-stock");

    // When all options are out of stock, suppress the "Sold out" message entirely
    if (stockStatus === "sold-out" && this.isAllSoldOut()) {
      this.hideStockStatus();
      return;
    }

    const message = this.stockMessages[stockStatus];

    if (message) {
      this.stockStatus.textContent = message;
      this.stockStatus.style.display = "block";
      // Reset any inline emphasis applied for the select-size warning
      this.stockStatus.style.color = "";
      this.stockStatus.style.fontWeight = "";
      this.stockStatus.classList.toggle("hover-status", isHover);
    } else {
      this.hideStockStatus();
    }
  }

  hideStockStatus() {
    if (this.stockStatus) {
      this.stockStatus.style.display = "none";
    }
  }

  setAtcSelectSizeStyle(isSelectSizeState) {
    if (!this.addToCartButton) return;
    this.addToCartButton.style.removeProperty('background-color');
  }

  isAllSoldOut() {
    // Returns true if every size option is out of stock/disabled
    if (!this.buttons || !this.buttons.length) return false;
    return Array.from(this.buttons).every((btn) => {
      const stock = btn.getAttribute("data-stock");
      return (
        btn.classList.contains("oos") ||
        btn.classList.contains("is-disabled") ||
        stock === "sold-out"
      );
    });
  }

  updateAddToCartButton(variantId) {
    if (!this.addToCartButton) return;

    // Determine stock status from the selected size button
    const btn = document.querySelector(`.size-button[data-value="${variantId}"]`);
    const isSoldOut = !!btn && (btn.classList.contains("oos") || btn.getAttribute("data-stock") === "sold-out");

    // Get the label element inside the ATC button if present
    const labelEl = this.addToCartButton.querySelector('[js-atc-btn-label]') || this.addToCartButton;

    if (!variantId) {
      // No variant selected
      this.addToCartButton.disabled = false;
      if (labelEl) labelEl.textContent = "Select Size";
      this.addToCartButton.classList.remove("is-sold-out");
      this.setAtcSelectSizeStyle(true);
      return;
    }

    if (isSoldOut) {
      // Selected variant is OOS
      this.addToCartButton.disabled = true;
      if (labelEl) labelEl.textContent = "Sold out";
      this.addToCartButton.classList.add("is-sold-out");
      this.setAtcSelectSizeStyle(false);
    } else {
      // Selected variant is in stock
      this.addToCartButton.disabled = false;
      if (labelEl) labelEl.textContent = "Add to cart";
      this.addToCartButton.classList.remove("is-sold-out");
      this.setAtcSelectSizeStyle(false);
    }
  }

  triggerVariantChange(variantId) {
    if (!this.select) return;
    this.select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  autoSelectFirstAvailable() {
    const firstAvailable = document.querySelector(
      ".size-button:not(.is-disabled)"
    );
    if (firstAvailable) {
      this.handleSelection(firstAvailable);
    }
  }

  bindVariantUpdates() {
    document.addEventListener("variant:change", (e) => {
      const variantId = e.detail.variant.id;
      this.buttons.forEach((button) => {
        const buttonVariantId = button.getAttribute("data-value");
        button.classList.toggle("is-selected", buttonVariantId == variantId);
      });
      this.updateStockStatus(variantId, false);
      this.updateAddToCartButton(variantId);
    });
  }
}

/**
 * Accordion Class
 */
class Accordion {
  constructor(containerSelector = ".product-accordion-info") {
    this.container = document.querySelector(containerSelector);
    if (!this.container) return;

    // Expand first item
    this.openItem(this.container.querySelector(".accordion-item"));

    this.container.addEventListener("click", (e) => {
      const header = e.target.closest(".accordion-header");
      if (!header) return;
      this.toggle(header.parentElement);
    });
  }

  openItem(item) {
    if (!item) return;
    item.classList.add("active");
    item.querySelector(".accordion-header").setAttribute("aria-expanded", "true");
    item.querySelector(".accordion-content").style.display = "block";
  }

  closeItem(item) {
    item.classList.remove("active");
    item.querySelector(".accordion-header").setAttribute("aria-expanded", "false");
    item.querySelector(".accordion-content").style.display = "none";
  }

  toggle(item) {
    const isActive = item.classList.contains("active");
    this.container.querySelectorAll(".accordion-item.active").forEach((el) => this.closeItem(el));
    if (!isActive) this.openItem(item);
  }
}

/**
 * Modal Class
 */
class Modal {
  constructor({
    modalSelector = ".shop-the-look-modal",
    overlaySelector = ".overlay",
    openButtonSelector = ".shop-the-look",
    closeButtonSelector = ".close-modal",
  } = {}) {
    const modal = document.querySelector(modalSelector);
    const overlay = document.querySelector(overlaySelector);
    if (!modal || !overlay) return;

    const openButton = document.querySelector(openButtonSelector);
    const closeButton = document.querySelector(closeButtonSelector);
    const body = document.body;

    const open = () => {
      modal.classList.add("modal--open");
      overlay.classList.add("overlay--open");
      body.classList.add("no-scroll");
      if (typeof initCarousel === "function") setTimeout(initCarousel, 100);
    };

    const close = () => {
      modal.classList.remove("modal--open");
      overlay.classList.remove("overlay--open");
      body.classList.remove("no-scroll");
    };

    openButton?.addEventListener("click", open);
    closeButton?.addEventListener("click", close);
    overlay.addEventListener("click", close);
  }
}

/**
 * ToggleContainer Class
 */
class ToggleContainer {
  constructor({
    toggleId = "pairs-toggle",
    defaultContainerSelector = ".pairs-with-container",
    pmContainerSelector = ".pairs-with-pm-container",
    buttonWrapperSelector = ".carousel-controls__button-wrapper",
  } = {}) {
    const toggle = document.getElementById(toggleId);
    const defaultContainer = document.querySelector(defaultContainerSelector);
    const pmContainer = document.querySelector(pmContainerSelector);
    const buttonWrapper = document.querySelector(buttonWrapperSelector);

    if (!toggle || !pmContainer) return;

    const refreshControls = (activeContainer) => {
      const slider = activeContainer?.querySelector(".js-carousel");
      if (!slider) return;
      slider.scrollLeft = 0;
      slider.dispatchEvent(new Event("scroll"));
      requestAnimationFrame(() => slider.dispatchEvent(new Event("scroll")));
    };

    const updateVisibility = () => {
      const isPm = toggle.checked;

      if (defaultContainer) defaultContainer.style.display = isPm ? "none" : "";
      pmContainer.style.display = isPm ? "" : "none";

      const activeContainer = isPm ? pmContainer : defaultContainer;
      if (buttonWrapper) {
        const productCount =
          activeContainer?.querySelectorAll(".product-card").length || 0;
        buttonWrapper.style.display = productCount > 2 ? "flex" : "none";
      }

      refreshControls(activeContainer);
    };

    toggle.addEventListener("change", updateVisibility);
    updateVisibility();
  }
}

/**
 * Gallery Class
 */
class Gallery {
  constructor(
    containerSelector = ".product-gallery",
    thumbnailSelector = ".product-media-thumbnail"
  ) {
    const container = document.querySelector(containerSelector);
    const thumbnails = document.querySelectorAll(thumbnailSelector);
    if (!container || !thumbnails.length) return;

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("click", () => {
        const index = thumbnail.getAttribute("data-index");
        const targetItem = container.querySelector(
          `.product-gallery-item[data-index="${index}"]`
        );
        targetItem?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "center",
        });
      });
    });
  }
}

// Initialize all components when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new SizeButtons();
  new Accordion();
  new Modal();
  new ToggleContainer({
    pairsWithCount: parseInt("{{ pairs_with_count }}", 10) || 0,
    pairsWithPmCount: parseInt("{{ pairs_with_pm_count }}", 10) || 0,
  });
  new Gallery();
});